import { QueryClient } from "@tanstack/react-query";
import { projectQueryKeys } from "../hooks/queries/useProjects";
import { orderQueryKeys } from "../hooks/queries/useOrders";
import { batchQueryKeys } from "../hooks/queries/useBatches";
import { productionStageQueryKeys } from "../hooks/queries/useProductionStages";
import { taskQueryKeys } from "../hooks/queries/useTasks";
import { customerQueryKeys } from "../hooks/queries/useCustomers";
import { supplierQueryKeys } from "../hooks/queries/useSuppliers";
import { qualityCheckQueryKeys } from "../hooks/queries/useQualityChecks";
import { qualityChecklistQueryKeys } from "../hooks/queries/useQualityChecklists";

/**
 * Service for handling cache invalidation based on entity types
 */
class CacheInvalidationService {
  private queryClient: QueryClient;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Invalidate cache for the specified entity types
   * @param entities List of entity types to invalidate
   */
  async invalidateEntities(entities: string[]): Promise<void> {
    console.log('[CacheInvalidationService] Invalidating entities:', entities);

    for (const entity of entities) {
      await this.invalidateEntity(entity);
    }
  }

  /**
   * Invalidate cache for a single entity type
   * @param entity Entity type to invalidate
   */
  private async invalidateEntity(entity: string): Promise<void> {
    switch (entity.toLowerCase()) {
      case 'projects':
        console.log('[CacheInvalidationService] Invalidating projects cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: projectQueryKeys.all 
        });
        break;

      case 'orders':
        console.log('[CacheInvalidationService] Invalidating orders cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: orderQueryKeys.all 
        });
        break;

      case 'batches':
        console.log('[CacheInvalidationService] Invalidating batches cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: batchQueryKeys.all 
        });
        break;

      case 'production_stages':
        console.log('[CacheInvalidationService] Invalidating production stages cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: productionStageQueryKeys.all 
        });
        break;

      case 'tasks':
        console.log('[CacheInvalidationService] Invalidating tasks cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: taskQueryKeys.all 
        });
        break;

      case 'customers':
        console.log('[CacheInvalidationService] Invalidating customers cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: customerQueryKeys.all 
        });
        break;

      case 'suppliers':
        console.log('[CacheInvalidationService] Invalidating suppliers cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: supplierQueryKeys.all 
        });
        break;

      case 'quality_checks':
        console.log('[CacheInvalidationService] Invalidating quality checks cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: qualityCheckQueryKeys.all 
        });
        break;

      case 'quality_checklists':
        console.log('[CacheInvalidationService] Invalidating quality checklists cache');
        await this.queryClient.invalidateQueries({ 
          queryKey: qualityChecklistQueryKeys.all 
        });
        break;

      default:
        console.warn(`[CacheInvalidationService] Unknown entity type for invalidation: ${entity}`);
    }
  }

  /**
   * Invalidate all caches
   */
  async invalidateAll(): Promise<void> {
    console.log('[CacheInvalidationService] Invalidating all caches');
    await this.queryClient.invalidateQueries();
  }
}

// Export singleton instance creator
export const createCacheInvalidationService = (queryClient: QueryClient) => {
  return new CacheInvalidationService(queryClient);
};